/**
 * Seat-removal UX — decision 16 / test-spec §5.2, the three-layer rule for
 * the one irreversible Phase 1 command.
 *
 * Two client-side layers live here (the engine layer is server-side):
 *
 *   Layer 2 — the client ALWAYS confirms. `renderRemoveSeatConfirm` is a
 *   modal the GM must accept before `removeSeat` is sent. Removal is FINAL
 *   (decision 17: no tombstone, no undo) whatever the target's state, so the
 *   confirm is unconditional. When the target is mid-reconnect-grace the
 *   confirm NAMES that fact specifically — it is the one thing the GM cannot
 *   otherwise see (`seat.reconnecting` is broadcast for exactly this).
 *
 *   Layer 3 — the removed player is TOLD, specifically. `renderSeatRemovedNotice`
 *   shows a dismissible message naming removal and the GM, never a generic
 *   connection error and never a silent reseat (E2E-13's worst outcome).
 *
 * Pure DOM + callbacks; the confirm never sends the command itself.
 */

import type { Seat } from "@asarimitsuha/table-core";
import { renderConfirmModal } from "./confirmModal.js";

export interface RemoveSeatConfirmCallbacks {
  /** GM accepted. Wiring maps this to sendCommand({ type: "removeSeat" }). */
  onConfirm(seatId: string): void;
  /** GM dismissed (button, ESC, or backdrop). Optional. */
  onCancel?(): void;
}

/**
 * Render the confirm modal for removing `seat` into `root` (a modal mount).
 * Thin caller of the shared §5.2 confirm primitive (`confirmModal.ts` —
 * cancel-focused, Escape cancels, focus-trapped); this module contributes
 * the removeSeat-specific truth: permanence, and the mid-reconnect warning
 * — the fact the GM cannot otherwise see.
 */
export function renderRemoveSeatConfirm(
  root: HTMLElement,
  seat: Seat,
  cb: RemoveSeatConfirmCallbacks,
): void {
  renderConfirmModal(
    root,
    {
      testid: "remove-seat-confirm",
      data: { seatId: seat.id },
      title: `Remove ${seat.name}?`,
      body:
        `This permanently deletes ${seat.name}’s seat. ` +
        `They can’t rejoin as ${seat.name}, and this can’t be undone.`,
      warnings: seat.reconnecting
        ? [
            {
              testid: "reconnecting-warning",
              text:
                `${seat.name} dropped a moment ago and may be reconnecting right now. ` +
                `Removing the seat will end that reconnection.`,
            },
          ]
        : [],
      confirmTestid: "confirm-remove",
      confirmLabel: `Remove ${seat.name}`,
      cancelTestid: "cancel-remove",
    },
    {
      onConfirm: () => cb.onConfirm(seat.id),
      onCancel: () => cb.onCancel?.(),
    },
  );
}

export interface SeatRemovedNotice {
  /** Display name of the GM who removed the seat, if known. Falls back to
   *  "the GM" — removeSeat is GM-only (CMD-11), so that is always true. */
  removedByName?: string | null;
  /** Called when the player dismisses the notice. Optional. */
  onDismiss?(): void;
}

/**
 * Render the removed-player notice into `root`. Specific by construction: it
 * names removal, names the GM, and makes clear the seat is gone — so the
 * player never mistakes it for a dropped connection or a fresh anonymous
 * reseat (E2E-13).
 */
export function renderSeatRemovedNotice(
  root: HTMLElement,
  notice: SeatRemovedNotice = {},
): void {
  root.replaceChildren();

  const by = notice.removedByName ?? "the GM";

  const panel = document.createElement("div");
  panel.dataset["testid"] = "seat-removed";
  panel.setAttribute("role", "alert");

  const heading = document.createElement("h2");
  heading.textContent = "You were removed from the table";
  panel.appendChild(heading);

  const body = document.createElement("p");
  body.textContent =
    `${by} removed your seat. Your character seat is gone and can’t be ` +
    `reclaimed. You can rejoin as a new player if you’d like.`;
  panel.appendChild(body);

  const dismiss = document.createElement("button");
  dismiss.type = "button";
  dismiss.dataset["testid"] = "dismiss-seat-removed";
  dismiss.textContent = "Dismiss";
  dismiss.addEventListener("click", () => {
    root.replaceChildren();
    notice.onDismiss?.();
  });
  panel.appendChild(dismiss);

  root.appendChild(panel);
}
